import React, { ChangeEvent, FC, JSX, useMemo, useState } from 'react';

import styled from 'styled-components';

// constants
import aampeTheme from '../../constants/aampeTheme';
import ICONS from '../../constants/icons';

// helper
import { PASSWORD_CHECKS, validatePassword } from '../../views/login/helper';

// components
import TextInput from './textInput';

export interface PasswordFieldProps {
    label: string;
    name: string;
    value: string;
    handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    touched?: boolean;
    error?: string;
    showValidationCheckList?: boolean;
    placeholder?: string;
}

const PasswordField: FC<PasswordFieldProps> = ({
    label,
    name,
    value,
    handleChange,
    handleBlur,
    touched,
    error,
    showValidationCheckList = true,
    placeholder = 'Password',
}) => {
    const [showPassword, setShowPassword] = useState<boolean>(false);

    const renderShowPasswordIcon = useMemo<JSX.Element>(() => {
        if (showPassword) {
            return (
                <ICONS.eye
                    color={aampeTheme.colors.textAccessibleWG01}
                    onClick={() => setShowPassword(false)}
                />
            );
        }
        return (
            <ICONS.eyeSlash
                color={aampeTheme.colors.textAccessibleWG01}
                onClick={() => setShowPassword(true)}
            />
        );
    }, [showPassword, setShowPassword]);

    const validationCheck = useMemo<Record<string, boolean>>(
        () => validatePassword(value),
        [value],
    );

    const renderCheckIcon = (isValid: boolean) => {
        if (isValid) {
            return (
                <ICONS.circleCheck
                    size={13}
                    color={aampeTheme.colors.green500}
                />
            );
        }
        return <ICONS.circleCross size={13} color={aampeTheme.colors.error} />;
    };

    return (
        <Container>
            <TextInput
                label={label}
                name={name}
                onChange={handleChange}
                onBlur={handleBlur}
                value={value}
                touched={touched}
                error={error}
                type={showPassword ? 'text' : 'password'}
                placeholder={placeholder}
                trailingIcon={renderShowPasswordIcon}
                inputStyle={{
                    width: '100%',
                }}
            />
            {showValidationCheckList && (
                <PasswordCheckList>
                    {Object.keys(PASSWORD_CHECKS).map((key, i) => (
                        <Item key={i}>
                            {renderCheckIcon(validationCheck[key])}
                            <Text>{PASSWORD_CHECKS[key]}</Text>
                        </Item>
                    ))}
                </PasswordCheckList>
            )}
        </Container>
    );
};

export default PasswordField;

const Container = styled.div({
    margin: aampeTheme.spacing4(5, 0, 5, 0),
});

const PasswordCheckList = styled.div({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
});

const Item = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
    marginTop: aampeTheme.spacing4(1.5),
});

const Text = styled.div({
    fontSize: aampeTheme.size.font.textSmall,
    color: aampeTheme.colors.green500,
});
